import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext, useEffect } from 'react';
import { Autoplay, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import LeadButton from '@/components/LeadButton';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';

import { TrustpilotReview } from '../data';
import { getAllReviews } from '../utils';

import 'swiper/css';
import 'swiper/css/pagination';

// 自定义 Swiper pagination 样式
const swiperStyles = `
  .reviews-swiper .swiper-pagination-bullet {
    background: #C6C7D2;
    opacity: 1;
  }
  .reviews-swiper .swiper-pagination-bullet-active {
    background: #0a0a0a;
  }
  .reviews-swiper .swiper-pagination {
    padding-right: 16px !important;
  }
`;

/**
 * ============================================
 * 样式规范 (根据 Figma 设计稿)
 * ============================================
 *
 * 【标题区域】
 * - XL (1920px): 字号 48px, 行高 1.2, Black
 * - LG (1024px): 字号 34px, 行高 1.2, Black
 * - Mobile: 字号 28px, 行高 1.2, Black
 *
 * 【卡片布局】
 * - XL: 3列, 卡片宽度 392px, 间距 24px
 * - LG: 3列, 卡片宽度 320px, 间距 16px
 * - Mobile: Swiper 轮播, 卡片宽度 344px, 间距 12px
 *
 * 【卡片样式】
 * - 背景: 白色
 * - 圆角: XL 16px, LG 12px, Mobile 12px
 * - 内边距: XL 24px, LG 20px, Mobile 16px
 * - 阴影: 无 (纯白背景)
 *
 * 【卡片内容】
 * 姓名:
 * - XL: 20px Bold
 * - LG: 16px Bold
 * - Mobile: 16px Bold
 *
 * 地区:
 * - XL: 16px Regular, 颜色 #1B1F4D
 * - LG: 14px Regular
 * - Mobile: 14px Regular
 *
 * 日期:
 * - XL: 16px Regular, 颜色 #1B1F4D
 * - LG: 14px Regular
 * - Mobile: 14px Regular
 *
 * 星级:
 * - XL: 20px (单个星星)
 * - LG: 16px
 * - Mobile: 16px
 *
 * 评论内容:
 * - XL: 16px Medium, 行高 1.4
 * - LG: 14px Medium, 行高 1.4
 * - Mobile: 16px Medium, 行高 1.4
 *
 * 【按钮】
 * - XL/LG: "Join for free"
 * - Mobile: "Try now"
 */

// 国家名 → 本地国旗图片路径映射（emoji 风格，跨平台兼容）
const COUNTRY_FLAG_URL: Record<string, string> = {
  Australia: '/images/wukong-reviews/Australia.png',
  'New Zealand': '/images/wukong-reviews/newZealand.png',
  'United States': '/images/wukong-reviews/unitedStates.png',
  Canada: '/images/wukong-reviews/canada.png',
  Singapore: '/images/wukong-reviews/singapore.png',
  Thailand: '/images/wukong-reviews/thailand.png',
  Germany: '/images/wukong-reviews/germany.png',
  'United Kingdom': '/images/wukong-reviews/unitedKindom.png',
  Belgium: '/images/wukong-reviews/Belgium.png',
};

/**
 * Trustpilot 星星评分组件
 */
const StarRating: React.FC<{ rating: number; size?: 'sm' | 'md' }> = ({
  rating,
  size = 'md',
}) => {
  const sizeClass = {
    sm: 'size-4',
    md: 'size-4 lg:size-3 xl:size-4',
  }[size];

  return (
    <div className="flex items-center space-x-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="12"
          viewBox="0 0 13 12"
          fill="none"
          className={sizeClass}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.46469 4.28077H12.0874L8.36405 6.93462L6.06169 8.56154L2.32035 11.2147L3.74134 6.9339L0 4.28005H4.62271L6.0437 0L7.46469 4.28077ZM8.66911 7.92476L6.04298 8.57885L9.76633 11.25L8.66911 7.92476Z"
            fill={i < rating ? '#F79B0F' : '#E5E5E5'}
          />
        </svg>
      ))}
    </div>
  );
};

/**
 * Trustpilot 图标
 */
const TrustpilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={className || 'size-5 lg:size-6'}>
    <path d="M0 0H20V20H0V0Z" fill="#00B67A" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.6541 8.44667H17.0038L12.6949 11.5133L10.0305 13.3933L5.70086 16.4592L7.3453 11.5125L3.01562 8.44584H8.36527L10.0097 3.5L11.6541 8.44667ZM13.048 12.6575L10.0089 13.4133L14.3177 16.5L13.048 12.6575Z"
      fill="white"
    />
  </svg>
);

/**
 * ============================================
 * 评论卡片组件 (统一 PC/Mobile)
 * ============================================
 *
 * 样式规范 (根据 Figma):
 * - 姓名: 黑色 (#0a0a0a), Bold
 * - 地区: 灰色 (#6B7280), Regular, 带竖线分隔符
 * - 分隔线: 使用 wk-uni-lib 主题变量
 * - 日期: 灰色 (#6B7280)
 * - 星星: #FFB800
 * - 评论内容: 黑色 (#0a0a0a), Medium
 */
interface ReviewCardProps {
  review: TrustpilotReview;
  variant?: 'desktop' | 'mobile';
  onClick?: () => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  variant = 'desktop',
  onClick,
}) => {
  const flagUrl = COUNTRY_FLAG_URL[review.region];
  const isMobile = variant === 'mobile';

  return (
    <a
      href={review.link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={`block cursor-pointer rounded-md border-[1px] border-border-normal bg-bg-card_white bg-clip-padding ${
        isMobile
          ? 'h-full space-y-3 p-4'
          : 'space-y-4 p-5 lg:space-y-4 lg:rounded-md lg:p-6 xl:space-y-5'
      }`}>
      <div className="flex flex-col">
        {/* 头部: 姓名、地区、Trustpilot 图标 */}
        <div className="flex items-center justify-between space-x-2">
          <div className="flex min-w-0 flex-1 items-center space-x-2">
            <span
              className={`truncate font-bold text-text-primary ${
                isMobile
                  ? 'text-[14px]'
                  : 'text-[14px] lg:text-[14px] xl:text-[14px]'
              }`}>
              {review.name}
            </span>
            <span className="h-3 w-px shrink-0 bg-border-deep" />
            <span
              className={`flex shrink-0 items-center whitespace-nowrap text-text-secondary_3 ${
                isMobile
                  ? 'text-[14px]'
                  : 'text-[14px] lg:text-[14px] xl:text-[14px]'
              }`}>
              {review.region}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              {flagUrl && (
                <img
                  src={flagUrl}
                  alt=""
                  className="ml-1 inline-block h-4 w-4"
                />
              )}
            </span>
          </div>
          <TrustpilotIcon
            className={`shrink-0 ${isMobile ? 'size-4' : 'lg: size-4 xl:size-5'}`}
          />
        </div>

        {/* 分隔线 */}
        <div className="my-4 h-px w-full bg-border-deep" />

        {/* 日期和星级 */}
        <div className="flex items-center space-x-3">
          <span
            className={`text-text-secondary_3 ${
              isMobile
                ? 'text-[14px] font-medium'
                : 'text-[14px] lg:text-[12px] xl:text-[14px]'
            }`}>
            {review.date}
          </span>
          <StarRating rating={review.rating} size={isMobile ? 'sm' : 'md'} />
        </div>

        {/* 评论内容 - XL: 18px, LG: 16px, Mobile: 16px */}
        <p
          className={`mt-3 font-medium leading-[1.4] text-text-primary ${
            isMobile
              ? 'line-clamp-4 text-[16px]'
              : 'text-[16px] lg:text-[14px] xl:text-[18px]'
          }`}>
          {review.comment}
        </p>
      </div>
    </a>
  );
};

/**
 * 评论区模块
 * 展示 Trustpilot 好评
 */
const ReviewsSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';
  const { isLogin } = useContext(GlobalLoginContext);
  const reviews = getAllReviews();

  // 约课按钮埋点
  const handleBookClassTrace = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-好评墙',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  // 好评墙点击埋点
  const handleReviewClick = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallOfLove_30Stories',
      },
    });
  }, []);

  // 注入自定义样式
  useEffect(() => {
    const styleId = 'reviews-swiper-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = swiperStyles;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  // PC 端分成 3 列
  const column1 = reviews.filter((_, i) => i % 3 === 0);
  const column2 = reviews.filter((_, i) => i % 3 === 1);
  const column3 = reviews.filter((_, i) => i % 3 === 2);

  return (
    <section
      id="reviews"
      className="flex w-full flex-col items-center space-y-6 px-4 py-[50px] lg:space-y-10 lg:px-8 lg:py-[70px] xl:space-y-12 xl:px-20 xl:py-[90px]"
      style={{ backgroundColor: '#F7F4ED' }}>
      {/* 标题 */}
      <h2
        className={`text-center text-[28px] font-black leading-normal text-text-primary lg:text-[34px] xl:text-[48px] [&>span]:text-brand-red-100 ${isZh ? 'font-sourceHanSans' : ''}`}
        dangerouslySetInnerHTML={{ __html: t('reviews-标题') }}
      />

      {/* PC 端 - 3列瀑布流布局 */}
      <div className="hidden w-full  max-w-[984px]  gap-4 lg:grid lg:grid-cols-3 xl:max-w-[1320px] xl:gap-6">
        {/* 第一列 */}
        <div className="flex flex-col space-y-4 xl:space-y-6">
          {column1.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              onClick={handleReviewClick}
            />
          ))}
        </div>
        {/* 第二列 */}
        <div className="flex flex-col space-y-4 xl:space-y-6">
          {column2.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              onClick={handleReviewClick}
            />
          ))}
        </div>
        {/* 第三列 */}
        <div className="flex flex-col space-y-4 xl:space-y-6">
          {column3.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              onClick={handleReviewClick}
            />
          ))}
        </div>
      </div>

      {/* 移动端 - Swiper 轮播 (每页3个卡片堆叠) */}
      <div className="w-full lg:hidden">
        <div className="-mr-4 overflow-visible">
          <Swiper
            modules={[Autoplay, Pagination]}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
            }}
            slidesPerView="auto"
            spaceBetween={12}
            slidesOffsetAfter={16}
            className="reviews-swiper !overflow-visible !pb-10">
            {/* 将评论分成每组3个 */}
            {Array.from({
              length: Math.ceil(reviews.slice(0, 18).length / 3),
            }).map((_, groupIndex) => {
              const groupReviews = reviews.slice(
                groupIndex * 3,
                groupIndex * 3 + 3,
              );
              return (
                <SwiperSlide
                  key={`group-${groupIndex}`}
                  className="!h-auto !w-[340px]">
                  <div className="flex flex-col space-y-3">
                    {groupReviews.map(review => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        variant="mobile"
                        onClick={handleReviewClick}
                      />
                    ))}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>

      {/* CTA 按钮 - PC 和移动端统一 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleBookClassTrace}>
        <LeadButton
          asCustomButton
          callbackUrl={
            typeof window !== 'undefined' ? window.location.href : ''
          }
          traceName="O_WallOfLove_ReviewsSection_CTAClick">
          <Button
            size="large"
            className="h-[52px] px-[30px] text-[20px] font-bold lg:h-[55px] lg:px-12 lg:text-[18px] xl:h-[77px] xl:px-[64px] xl:text-[24px]">
            {t('reviews-按钮')}
          </Button>
        </LeadButton>
      </div>
    </section>
  );
};

export default ReviewsSection;
